import React, { FC, Fragment, ReactNode, useEffect, useState } from "react";
import SB from "@components/common/Sidebar";
import { User } from "./User";
import { Calendar, LanguageLevel, SupplierByIdQuery } from "@graphql/types";
import { Trans, t } from "@helpers/translate";
import { getDateDiff } from "@helpers/getDateDiff";
import classNames from "classnames";
import { useRouter } from "next/router";
import { InputDate } from "../TourPage/Sidebar/InputDate";
import { DateTime } from "luxon";
import { graphql } from "@helpers/graphql";
import { useQuery } from "react-query";
import { Divider } from "@components/ui/Divider";
import { Button } from "@components/ui/Button";
import { Scarcity } from "@components/ui/Scarcity";
import { useAuth } from "@helpers/hooks/useAuth";
import { convertUrlParamsToQueryParam, ROUTES } from "@root/routes";
import { InputPeople } from "@components/common/Inputs/InputPeople";

type Props = {
  supplier: SupplierByIdQuery["supplierById"];
  variant?: "default" | "sidebar";
  showSendMessage?: boolean;
  disableRatingClick?: boolean;
  showFilters?: boolean;
  showScarcity?: boolean;
  showVerifiedData?: boolean;
};

const DefaultWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className="mb-8">{children}</div>;
};

export const Sidebar: FC<Props> = ({
  supplier,
  variant = "sidebar",
  showSendMessage = true,
  disableRatingClick = false,
  showFilters = false,
  showScarcity = false,
  showVerifiedData = false,
}) => {
  const router = useRouter();
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [people, setPeople] = useState({
    adults: 0,
    children: 0,
    infants: 0,
  });
  const [calendar, setCalendar] = useState<Calendar | undefined>(undefined);

  const { data } = useQuery(["supplierCalendarById", supplier?.id], () => {
    if (supplier?.id) {
      return graphql.SupplierCalendarById({ id: supplier.id });
    }
    return undefined;
  });

  useEffect(() => {
    data && setCalendar(data?.supplierById?.calendar);
  }, [data]);

  useEffect(() => {
    const params = router.query;

    setPeople({
      adults: params.adults ? Number(params.adults) : 2,
      children: Number(params.children || 0),
      infants: Number(params.infants || 0),
    });
    setDate(params.date ? new Date(String(params.date)) : undefined);
  }, [router.isReady]);

  if (!supplier) {
    return <Fragment />;
  }

  const calendarUpdatedAt = getDateDiff(supplier.calendarUpdatedAt);
  const Wrapper = variant == "default" ? DefaultWrapper : SB;
  const containerClassName = classNames({
    "lg:hidden": variant == "default",
  });
  const buttonClassName = classNames("px-2", {
    "fixed bottom-0 left-0 right-0 z-10 py-4 border-t border-gray-200 w-full bg-white lg:hidden":
      variant === "default",
    "pt-1": variant === "sidebar",
  });

  const mountQuery = () => {
    const params = new URLSearchParams();
    date &&
      params.append("date", DateTime.fromJSDate(date).toFormat("yyyy-MM-dd"));
    people.adults && params.append("adults", `${people.adults}`);
    people.children && params.append("children", `${people.children}`);
    people.infants && params.append("infants", `${people.infants}`);
    return params;
  };

  const { auth } = useAuth();

  const sendMessage = () => {
    if (auth) {
      router.push(
        ROUTES.supplier(supplier.id).message.form(
          convertUrlParamsToQueryParam(mountQuery())
        )
      );
      return;
    }

    router.push(
      ROUTES.supplier(supplier.id).message.signup(
        convertUrlParamsToQueryParam(mountQuery())
      )
    );
  };

  return (
    <Wrapper>
      <User
        displayName={supplier.displayName}
        picture={supplier.picture?.url}
        reviewsCount={supplier?.stats?.reviewsCount}
        rating={supplier?.stats?.rating}
        createdAt={supplier.createdAt}
        onRatingClicked={
          disableRatingClick
            ? undefined
            : () => {
                router.push("#reviews");
              }
        }
        showVerifiedData={showVerifiedData}
      />

      <div className={containerClassName}>
        {showFilters && (
          <div className="hidden lg:block">
            <Divider className="my-3" />
            <div>
              <InputDate
                defaultValue={date}
                calendar={calendar}
                onApply={(value) => setDate(value)}
              />

              <InputPeople
                className="mt-2"
                minPeople={1}
                maxPeople={supplier?.maxAllowedPeople || 6}
                defaultValue={{
                  adults: people.adults,
                  children: people.children,
                  infants: people.infants,
                }}
                onApply={(value) => setPeople(value)}
                onReset={() => {
                  setPeople({
                    adults: 2,
                    children: 0,
                    infants: 0,
                  });
                }}
              />
            </div>
            <Divider className="my-3" />
          </div>
        )}
        {showScarcity && (
          <div className="hidden lg:block">
            <SupplierScarcity supplierId={supplier.id} date={date} />
          </div>
        )}
        {showSendMessage && (
          <>
            <div className={buttonClassName}>
              <Button
                onClick={sendMessage}
                className="w-full cursor-pointer"
                rounded
                variant="blue"
                disabled={supplier.isBan}
                title={t({
                  id: "common.link.title",
                  message: "GoWithGuide - Private Tours & Local Tour Guides",
                })}
              >
                <Trans id="supplier.message.message">Message</Trans>{" "}
                {supplier.displayName}
              </Button>
            </div>
            <Divider className="my-3" />
          </>
        )}
        <SB.Item
          icon="marker"
          title={t({ id: "supplier.areas", message: "Available Areas" })}
        >
          {supplier.cities
            .map((city) =>
              supplier?.city?.id === city.id
                ? `${city.name} (Living)`
                : city.name
            )
            .join(", ")}
        </SB.Item>
        <Divider className="my-3" />
        <SB.Item
          icon="chat"
          title={t({ id: "supplier.languages", message: "Languages" })}
        >
          {supplier.languages
            .map((item) =>
              item.level === LanguageLevel.Native
                ? `${item.language.name} (Native)`
                : item.language.name
            )
            .join(", ")}
        </SB.Item>

        {supplier.responseTimeMessage && (
          <>
            <Divider className="my-3" />
            <SB.Item
              icon="time"
              title={t({
                id: "supplier.response-time",
                message: "Response Time",
              })}
            >
              {supplier.responseTimeMessage}
            </SB.Item>
          </>
        )}

        <Divider className="my-3" />
        <SB.Item
          icon="calendar"
          title={t({
            id: "supplier.availability-updated-new",
            message: "Availability Updated",
          })}
        >
          {calendarUpdatedAt}
        </SB.Item>
      </div>
    </Wrapper>
  );
};

interface SupplierScarcityProps {
  supplierId: number;
  date?: Date;
}

const SupplierScarcity: FC<SupplierScarcityProps> = ({ supplierId, date }) => {
  const variables = { supplierId, date };

  const { data } = useQuery(["supplierScarcity", variables], () => {
    return graphql.supplierScarcity({
      supplierId: supplierId,
      date: date ? DateTime.fromJSDate(date).toISODate() : null,
    });
  });

  if (!data || !data.supplierScarcity) {
    return <div></div>;
  }

  return (
    <Scarcity
      slot={data.supplierScarcity?.slot || 0}
      month={data.supplierScarcity?.month || ""}
    />
  );
};
